import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import SaveIcon from '@material-ui/icons/Save';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import Alert from '@material-ui/lab/Alert';
import { assoc, assocPath, dissoc } from 'ramda';
import { useToggle } from 'react-use';
import { Settings } from '@material-ui/icons';

import { getUseCreateCustomerBeta } from 'reducers/configs/settings';
import { saveTempSetting } from 'actions/configs';

import {
  API_REQUIRED_FIELDS,
  CUSTOMER_TYPES,
  CustomerType,
  FORM_FIELDS,
} from '../types';

const FormFieldConfiguration = ({
  field,
  label,
  config = {} as any,
  update,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState(config.label ?? label);

  const resetTitle = () => {
    update(field, dissoc('label', config));
    setTitle(label);
  };

  const saveTitle = () => {
    if (title !== label) {
      update(field, assoc('label', title, config));
    } else {
      resetTitle();
    }
    setEditMode(false);
  };

  return (
    <Grid key={field} item xs={12} container>
      <Grid item xs={8}>
        {editMode ? (
          <TextField value={title} onChange={e => setTitle(e.target.value)} />
        ) : (
          title ?? label
        )}
      </Grid>
      <Grid item xs={1} style={{ textAlign: 'end' }}>
        {editMode ? (
          <Button onClick={saveTitle}>
            <SaveIcon color="secondary" />
          </Button>
        ) : (
          <Button onClick={() => setEditMode(true)}>
            <EditIcon color="primary" />
          </Button>
        )}
      </Grid>
      <Grid item xs={1} style={{ textAlign: 'end' }}>
        {config.label && (
          <Button onClick={resetTitle}>
            <DeleteOutlinedIcon color="error" />
          </Button>
        )}
      </Grid>
      <Grid item xs={1} style={{ textAlign: 'end' }}>
        <Checkbox
          disabled={API_REQUIRED_FIELDS.includes(field)}
          checked={config?.enabled ?? false}
          onChange={() =>
            update(field, assoc('enabled', !config.enabled, config))
          }
        />
      </Grid>
      <Grid item xs={1} style={{ textAlign: 'end' }}>
        <Checkbox
          disabled={API_REQUIRED_FIELDS.includes(field)}
          checked={config?.required ?? false}
          onChange={() =>
            update(field, assoc('required', !config.required, config))
          }
        />
      </Grid>
    </Grid>
  );
};

const CustomerFormConfiguration = ({ config, updateConfig }) => {
  const dispatch = useDispatch();
  const [showConfig, toggleConfig] = useToggle(false);
  const { t } = useTranslation('createCustomer');

  const isConfigurableCustomerFromEnabled = useSelector(
    getUseCreateCustomerBeta,
  );
  const toggleCustomerFormBeta = () => {
    dispatch(
      saveTempSetting(
        'pos_brazil_use_customer_creation_beta',
        !isConfigurableCustomerFromEnabled,
      ),
    );
  };

  const [customerType, setCustomerType] = useState<CustomerType>(
    CUSTOMER_TYPES.PERSON,
  );

  const updateConfigPerField = (field, config) => {
    updateConfig(assocPath(['formFields', customerType, field], config));
  };

  const updateAllowedCustomerType = e => {
    updateConfig(assoc('allowedCustomerTypes', e.target.value));
  };

  return (
    <Grid item xs={12} container spacing={1}>
      <Grid container>
        <Grid item>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isConfigurableCustomerFromEnabled}
                  onChange={toggleCustomerFormBeta}
                />
              }
              label="Enable Configurable Customer Form"
            />
          </FormControl>
        </Grid>
        <Grid item xs />
        <Grid item>
          <IconButton onClick={toggleConfig}>
            <Settings />
          </IconButton>
        </Grid>
      </Grid>
      <Collapse in={showConfig}>
        <Grid item xs={12}>
          <h2>Configurable Customer Form</h2>
        </Grid>
        <Grid item xs={12}>
          <p>
            Configurable customer form allows you to specify which fields should
            be visible on the customer creation form and which of them should be
            required. You can also customize their names so would be more
            descriptive and prompting to you.
          </p>
        </Grid>
        <Grid item xs={12}>
          <Alert color="warning">
            <WarningIcon /> Enabling Configurable Customer Form would break all
            Customer Creation plugins. Either use the legacy Customer Creation
            Form with plugins or the new Configurable Customer Form. For more
            information, please, contact our support team.
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              POS user should be able to create:
            </FormLabel>
            <RadioGroup
              aria-label="allowed customer type"
              defaultValue=""
              value={config.allowedCustomerTypes}
              onChange={updateAllowedCustomerType}
            >
              <FormControlLabel
                value={CUSTOMER_TYPES.PERSON}
                control={<Radio />}
                label="Person"
              />
              <FormControlLabel
                value={CUSTOMER_TYPES.COMPANY}
                control={<Radio />}
                label="Company"
              />
              <FormControlLabel
                value=""
                control={<Radio />}
                label="PERSON and COMPANY"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <p>
            Select which fields to show when creating/editing customer at the
            POS
          </p>
        </Grid>
        <Grid item xs={12}>
          <TextField
            select
            label="Customer type"
            variant="outlined"
            fullWidth
            onChange={e => setCustomerType(e.target.value as CustomerType)}
            value={customerType}
          >
            <MenuItem value={CUSTOMER_TYPES.PERSON}>
              {CUSTOMER_TYPES.PERSON}
            </MenuItem>
            <MenuItem value={CUSTOMER_TYPES.COMPANY}>
              {CUSTOMER_TYPES.COMPANY}
            </MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={8}>
            <span style={{ fontWeight: 'bold' }}>Field Name</span>
          </Grid>
          <Grid item xs={1} style={{ textAlign: 'end' }}>
            <span style={{ fontWeight: 'bold' }}>Rename</span>
          </Grid>
          <Grid item xs={1} style={{ textAlign: 'end' }}>
            <span style={{ fontWeight: 'bold' }}>Reset</span>
          </Grid>
          <Grid item xs={1} style={{ textAlign: 'end' }}>
            <span style={{ fontWeight: 'bold' }}>Enabled</span>
          </Grid>
          <Grid item xs={1} style={{ textAlign: 'end' }}>
            <span style={{ fontWeight: 'bold' }}>Required</span>
          </Grid>
        </Grid>
        {(FORM_FIELDS[customerType] as string[]).map(field => {
          const fieldConfig = config?.formFields?.[customerType]?.[field];
          return (
            <FormFieldConfiguration
              key={field}
              field={field}
              label={t(`fields.${field}`)}
              config={fieldConfig}
              update={updateConfigPerField}
            />
          );
        })}
      </Collapse>
    </Grid>
  );
};

export default CustomerFormConfiguration;
